import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { ThreeSixtyViewer } from './360viewerLibrary/ThreeSixtyViewer';
import { ViewerBimServiceService } from './viewer-bim-service.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenshotService {
  constructor(
    private toaster: ToastrService,
    private translocoService: TranslocoService,
  ) {}

  /**
   * Takes a screenshot based on the current viewer mode
   * @param threeSixtyViewer - The 360 viewer instance
   * @param bimService - The BIM service instance
   * @param isBimSplit - Whether BIM split mode is active
   */
  async takeScreenshot(
    threeSixtyViewer: ThreeSixtyViewer,
    bimService: ViewerBimServiceService,
    isBimSplit: boolean,
  ): Promise<void> {
    if (isBimSplit) {
      await this.takeCombinedScreenshot(threeSixtyViewer, bimService);
    } else {
      await this.takeSingleScreenshot(threeSixtyViewer);
    }
  }

  /**
   * Takes a single screenshot from the 360 viewer
   */
  private async takeSingleScreenshot(threeSixtyViewer: ThreeSixtyViewer): Promise<void> {
    try {
      const blob = await threeSixtyViewer.getScreenshot();
      const url = URL.createObjectURL(blob);
      this.downloadScreenshot(url, true);
      this.toaster.success(this.translocoService.translate('Screenshot exported successfully'));
    } catch (error) {
      this.toaster.error(this.translocoService.translate('Failed to capture screenshot'));
    }
  }

  /**
   * Takes combined screenshots from both 360 viewer and BIM viewer
   */
  private async takeCombinedScreenshot(
    threeSixtyViewer: ThreeSixtyViewer,
    bimService: ViewerBimServiceService,
  ): Promise<void> {
    try {
      // Capture both screenshots
      const [bimBase64, viewerBlob] = await Promise.all([
        bimService.captureForgeScreenshot(),
        threeSixtyViewer.getScreenshot(),
      ]);

      // Convert viewer blob to base64
      const viewerBase64 = await this.blobToBase64(viewerBlob);

      // Combine screenshots side by side
      const combinedBase64 = await this.combineScreenshots(viewerBase64, bimBase64);

      // Download the combined screenshot
      this.downloadScreenshot(combinedBase64, false);

      this.toaster.success(this.translocoService.translate('Screenshot exported successfully'));
    } catch (error) {
      this.toaster.error(this.translocoService.translate('Failed to capture screenshot'));
    }
  }

  /**
   * Converts a blob to base64 string
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Combines two base64 images side by side
   */
  private async combineScreenshots(viewerBase64: string, bimBase64: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const viewerImage = new Image();
      const bimImage = new Image();

      let imagesLoaded = 0;

      const onImageLoad = () => {
        imagesLoaded++;
        if (imagesLoaded === 2) {
          // Determine the target dimensions (use the larger dimensions)
          const targetWidth = Math.max(viewerImage.width, bimImage.width);
          const targetHeight = Math.max(viewerImage.height, bimImage.height);

          // Set canvas size to accommodate both images side by side with same dimensions
          canvas.width = targetWidth * 2;
          canvas.height = targetHeight;

          // BIM on left, Viewer on right
          ctx.drawImage(bimImage, 0, 0, targetWidth, targetHeight);
          ctx.drawImage(viewerImage, targetWidth, 0, targetWidth, targetHeight);

          // Export as base64
          resolve(canvas.toDataURL('image/png'));
        }
      };

      const onError = () => {
        reject(new Error('Failed to load images for combining'));
      };

      viewerImage.onload = onImageLoad;
      viewerImage.onerror = onError;
      viewerImage.src = viewerBase64;

      bimImage.onload = onImageLoad;
      bimImage.onerror = onError;
      bimImage.src = bimBase64;
    });
  }

  /**
   * Downloads a screenshot
   * @param data - Either a blob URL or base64 data URL
   * @param isBlobUrl - Whether the data is a blob URL that needs to be revoked
   */
  private downloadScreenshot(data: string, isBlobUrl: boolean = false): void {
    const link = document.createElement('a');
    link.href = data;
    link.download = `screenshot_${new Date().toISOString()}.png`;
    link.click();

    if (isBlobUrl) {
      URL.revokeObjectURL(data);
    }
  }
}
